import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import "./main.scss";
import { agmsClientRequestGet } from "../../apis/request";
import AdminLayout from "./AdminLayout";
import secureLocalStorage from "react-secure-storage";
import { formatServerTime, parseServerTime } from "../../utils/serverTime";

// 혈당 데이터 체크 — 세션별 DB 저장값(user_glucose, 10분마다 그때 표로 계산해 쌓인 값)과
// 지금 표로 다시 계산한 값(/api/graph/daily)을 시각별로 비교해, 어긋난 구간·다른 점 목록·정리 SQL 을 보여준다(그래프 없음).
// 감도표를 잘못 넣었다 고쳤을 때 "언제부터 어긋났는지" 찾는 용도. 화면·DB 는 바꾸지 않음(조회만).
// 열람: 오원준(5002)만 (AdminLayout 메뉴 + 이 화면 둘 다 제한).
export const GLUCOSE_CHECK_VIEWER_IDS = [5002];
export const canViewGlucoseCheck = () =>
  Number(secureLocalStorage.getItem("role")) === 1001
  && GLUCOSE_CHECK_VIEWER_IDS.includes(Number(secureLocalStorage.getItem("authUser")));

const PAGE_SIZE = 10;
const RECENT_UNSAVED_MIN = 15; // 재계산에만 있는 점이 '지금' 기준 이 시간 안이면 아직 저장 전(정상)

const thBase = {
  backgroundColor: "#787397", color: "#fff", fontWeight: "bold", fontFamily: "Noto Sans CJK KR",
  padding: "10px 16px", borderRight: "1px solid #EBEBEB", textAlign: "center", whiteSpace: "nowrap",
};
const tdBase = {
  backgroundColor: "#fff", textAlign: "center", borderRight: "1px solid #EBEBEB",
  borderBottom: "1px solid #EBEBEB", padding: "8px 12px",
};
const btn = (primary, disabled) => ({
  padding: "6px 14px", borderRadius: "6px", border: "1px solid #787397",
  background: primary ? "#787397" : "#fff", color: primary ? "#fff" : "#5E2589",
  cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1, whiteSpace: "nowrap",
});

const fmt = (ms) => formatServerTime(new Date(ms).toISOString(), "YYYY-MM-DD HH:mm", "-");
const minuteKey = (v) => { const m = parseServerTime(v); return m ? Math.floor(m.valueOf() / 60000) * 60000 : null; };

// 두 시리즈({분 ms: 값})를 비교 → 요약·다른 점·어긋난 구간
const compare = (stored, recalc) => {
  const keys = Array.from(new Set([...stored.keys(), ...recalc.keys()])).sort((a, b) => a - b);
  const now = Date.now();
  let same = 0;
  const diffs = []; const onlyDb = []; const onlyRecalc = []; const onlyRecalcOld = [];
  keys.forEach((k) => {
    const d = stored.get(k); const r = recalc.get(k);
    if (d !== undefined && r !== undefined) {
      if (Number(d) === Number(r)) same++; else diffs.push({ t: k, db: d, re: r, gap: Number(r) - Number(d) });
    } else if (d !== undefined) onlyDb.push(k);
    else {
      onlyRecalc.push(k);
      if (now - k > RECENT_UNSAVED_MIN * 60000) onlyRecalcOld.push(k);
    }
  });
  // 연속된 '다름' 묶기(사이 빈칸 30분 이하는 같은 구간)
  const ranges = [];
  diffs.forEach((x) => {
    const last = ranges[ranges.length - 1];
    if (last && x.t - last.end <= 30 * 60000) { last.end = x.t; last.n++; }
    else ranges.push({ start: x.t, end: x.t, n: 1 });
  });
  const maxGap = diffs.reduce((m, x) => (Math.abs(x.gap) > Math.abs(m) ? x.gap : m), 0);
  return { same, diffs, onlyDb, onlyRecalc, onlyRecalcOld, ranges, maxGap };
};

// 정리 SQL(텍스트만) — 어긋난 구간 백업 → 삭제. 다음 10분 저장 때 지금 표로 다시 채워짐. 실행은 사람.
const buildSql = (userDeviceId, startMs, endMs) => {
  const s = new Date(startMs).toISOString(); const e = new Date(endMs + 60000).toISOString();
  const d = new Date(); const tag = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
  const bk = `backup_user_glucose_${userDeviceId}_check_${tag}`;
  const where = `user_device_id = ${userDeviceId} AND measured_at >= '${s}' AND measured_at < '${e}'`;
  return `-- 개인용 운영 DB — 세션 ${userDeviceId} 어긋난 구간 ${fmt(startMs)} ~ ${fmt(endMs)} (브라우저 시간) 저장값 지우기
-- 지우면 다음 10분 저장 때 지금 감도표로 다시 채워짐. 🚨 운영 DB — 사람이 실행, 4단계.
-- ① 개수
SELECT count(*) FROM user_glucose WHERE ${where};
-- ② 백업
CREATE TABLE ${bk} AS SELECT * FROM user_glucose WHERE ${where};
SELECT count(*) AS backup_rows FROM ${bk};
-- ③ 삭제
BEGIN;
DELETE FROM user_glucose WHERE ${where};
COMMIT;
-- ④ 확인(0) — 10분 뒤 다시 '데이터 체크'로 다시 채워졌는지 확인
SELECT count(*) FROM user_glucose WHERE ${where};
-- 되돌리기: INSERT INTO user_glucose SELECT * FROM ${bk};
`;
};

const AdminGlucoseCheck = () => {
  const allowed = canViewGlucoseCheck();
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState(null);
  const [userPage, setUserPage] = useState(1);
  const [userPages, setUserPages] = useState(1);
  const [user, setUser] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [checking, setChecking] = useState(null); // started_dt
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showSql, setShowSql] = useState(false);
  const [diffPage, setDiffPage] = useState(1);

  // 목록 순서는 서버 기준(사용자 → 최고관리자 → 일반관리자, 최신 가입 먼저)
  const loadUsers = async (s, p = 1) => {
    setError(null);
    try {
      const res = await agmsClientRequestGet(`/api/admin/users?page=${p}&size=${PAGE_SIZE}&search=${encodeURIComponent(s || "")}`);
      setUsers(res && Array.isArray(res.content) ? res.content : []);
      setUserPage(p);
      setUserPages(res && res.totalPages ? res.totalPages : 1);
    } catch (e) { setError("유저 목록을 불러오지 못했습니다."); setUsers([]); }
  };

  useEffect(() => { if (allowed) loadUsers("", 1); }, [allowed]);

  const pickUser = async (u) => {
    setUser(u); setSessions(null); setResult(null); setError(null);
    try {
      // 목록 표시는 기존 측정목록과 같은 API(/api/device/all — 그래프 API 와 같은 started_dt), user_device_id 는 관리자 API 에서 붙임
      const [list, admin] = await Promise.all([
        agmsClientRequestGet(`/api/device/all?user_id=${u.id}`),
        agmsClientRequestGet(`/api/admin/users/${u.id}/devices`),
      ]);
      const idByStart = {};
      (Array.isArray(admin) ? admin : []).forEach((a) => { idByStart[minuteKey(a.started_dt)] = a.user_device_id; });
      setSessions((Array.isArray(list) ? list : []).filter((it) => it && it.started_dt)
        .map((it) => ({ ...it, user_device_id: idByStart[minuteKey(it.started_dt)] })));
    } catch (e) { setError("측정목록을 불러오지 못했습니다."); setSessions([]); }
  };

  const runCheck = async (s) => {
    setChecking(s.started_dt); setResult(null); setError(null); setShowSql(false); setDiffPage(1);
    try {
      const q = `user_id=${user.id}&started_dt=${encodeURIComponent(s.started_dt)}`;
      const [stored, daily] = await Promise.all([
        agmsClientRequestGet(`/api/graph/glucose/stored?${q}`),
        agmsClientRequestGet(`/api/graph/daily?${q}`),
      ]);
      const dbMap = new Map();
      (Array.isArray(stored) ? stored : []).forEach((p) => { const k = minuteKey(p.time); if (k !== null) dbMap.set(k, p.glucose); });
      const reMap = new Map();
      ((daily && daily.dayGraphData) || []).forEach((day) => {
        const ts = day.dateTimeList || []; const vs = day.valueList || [];
        ts.forEach((t, i) => { if (vs[i] !== null && vs[i] !== undefined) { const k = minuteKey(t); if (k !== null) reMap.set(k, vs[i]); } });
      });
      if (dbMap.size === 0 && reMap.size === 0) { setError("이 세션은 저장값·재계산값이 모두 없습니다."); return; }
      setResult({ session: s, dbMap, reMap, ...compare(dbMap, reMap) });
    } catch (e) { setError("데이터 체크 실패 (권한 또는 서버 오류)"); }
    finally { setChecking(null); }
  };

  const downloadDiffCsv = () => {
    const rows = ["time,db,recalc,diff", ...result.diffs.map((x) => `${fmt(x.t)},${x.db},${x.re},${x.gap}`)];
    const url = window.URL.createObjectURL(new Blob(["﻿" + rows.join("\n")], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a"); a.href = url;
    a.setAttribute("download", `glucose_check_${result.session.user_device_id || ""}_${String(result.session.started_dt).replace(/[^0-9]/g, "")}.csv`);
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
  };

  if (!allowed) {
    return (
      <AdminLayout>
        <div style={{ padding: "40px", color: "#888" }}>접근 권한이 없습니다.</div>
      </AdminLayout>
    );
  }

  const first = result && result.diffs.length ? result.diffs[0].t : null;
  const last = result && result.diffs.length ? result.diffs[result.diffs.length - 1].t : null;
  const diffPages = result ? Math.max(1, Math.ceil(result.diffs.length / 50)) : 1;
  // Claude 가 읽는 결과 블록(id=glucose-check-result) — 검사 요청 시 Claude 가 이 화면을 열어 이 JSON 으로 판정·정리 SQL 범위를 잡는다
  const machineResult = result ? JSON.stringify({
    user_id: user && user.id, user_name: user && user.name,
    user_device_id: result.session.user_device_id || null, serial: result.session.serial_number, started_dt: result.session.started_dt,
    same: result.same, diff: result.diffs.length, only_db: result.onlyDb.length,
    only_recalc: result.onlyRecalc.length, only_recalc_older_than_15m: result.onlyRecalcOld.length,
    first_diff: first !== null ? new Date(first).toISOString() : null, last_diff: last !== null ? new Date(last).toISOString() : null,
    max_gap: result.maxGap,
    ranges: result.ranges.map((r) => ({ start: new Date(r.start).toISOString(), end: new Date(r.end).toISOString(), n: r.n })),
  }, null, 1) : "";

  return (
    <AdminLayout>
      <div style={{ padding: "24px 32px" }}>
        <h4 style={{ marginBottom: "6px" }}>혈당 데이터 체크</h4>
        <div style={{ color: "#777", fontSize: "13px", marginBottom: "18px" }}>
          DB 저장값(그때 감도표로 계산돼 쌓인 값)과 지금 감도표로 다시 계산한 값을 비교합니다. 조회만 하며 화면·DB 는 바뀌지 않습니다.
        </div>

        {error && <div style={{ color: "#c62828", marginBottom: "12px" }}>{error}</div>}

        {/* 1) 사용자 */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
          <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") loadUsers(search, 1); }}
            placeholder="이름·이메일 검색" style={{ padding: "6px 10px", border: "1px solid #d5d0dd", borderRadius: "6px", width: "260px" }} />
          <button style={btn(true)} onClick={() => loadUsers(search, 1)}>검색</button>
        </div>
        <table style={{ borderCollapse: "collapse", marginBottom: "8px" }}>
          <thead><tr><th style={thBase}>이름</th><th style={thBase}>이메일</th><th style={thBase}>권한</th><th style={thBase}></th></tr></thead>
          <tbody>
            {(users || []).map((u) => (
              <tr key={u.id}>
                <td style={{ ...tdBase, fontWeight: user && user.id === u.id ? "bold" : "normal" }}>{u.name}</td>
                <td style={tdBase}>{u.email}</td>
                <td style={tdBase}>{u.role_name}</td>
                <td style={tdBase}><button style={btn(false)} onClick={() => pickUser(u)}>선택</button></td>
              </tr>
            ))}
            {users && users.length === 0 && <tr><td colSpan={4} style={{ ...tdBase, color: "#888" }}>검색 결과가 없습니다.</td></tr>}
          </tbody>
        </table>
        <div style={{ display: "flex", gap: "6px", alignItems: "center", marginBottom: "20px" }}>
          <button style={btn(false, userPage <= 1)} disabled={userPage <= 1} onClick={() => loadUsers(search, userPage - 1)}>이전</button>
          <span>{userPage} / {userPages}</span>
          <button style={btn(false, userPage >= userPages)} disabled={userPage >= userPages} onClick={() => loadUsers(search, userPage + 1)}>다음</button>
        </div>

        {/* 2) 측정목록 리스트 + 데이터 체크 */}
        {user && (
          <>
            <div style={{ fontSize: "18px", marginBottom: "8px" }}>측정목록 리스트 — {user.name}</div>
            <table style={{ borderCollapse: "collapse", marginBottom: "24px" }}>
              <thead><tr>
                <th style={thBase}>세션</th><th style={thBase}>시리얼 넘버</th><th style={thBase}>측정 기간</th>
                <th style={thBase}>시작 날짜</th><th style={thBase}>종료 날짜</th><th style={thBase}>데이터 체크</th>
              </tr></thead>
              <tbody>
                {sessions === null && <tr><td colSpan={6} style={tdBase}>불러오는 중…</td></tr>}
                {sessions && sessions.length === 0 && <tr><td colSpan={6} style={{ ...tdBase, color: "#888" }}>측정 이력이 없습니다.</td></tr>}
                {(sessions || []).map((s) => (
                  <tr key={s.started_dt}>
                    <td style={tdBase}>{s.user_device_id || "-"}</td>
                    <td style={tdBase}>{s.serial_number}</td>
                    <td style={tdBase}>{s.duration} 일</td>
                    <td style={tdBase}>{formatServerTime(s.started_dt, "YYYY-MM-DD HH:mm:ss", s.started_dt)}</td>
                    <td style={tdBase}>{!s.last_dt ? "---" : formatServerTime(s.last_dt, "YYYY-MM-DD HH:mm:ss", s.last_dt)}</td>
                    <td style={tdBase}>
                      <button style={btn(true, checking !== null)} disabled={checking !== null} onClick={() => runCheck(s)}>
                        {checking === s.started_dt ? "계산 중…" : "체크"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* 3) 결과 */}
        {result && (
          <div style={{ border: "1px solid #d5d0dd", borderRadius: "8px", padding: "16px 20px" }}>
            <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
              세션 {result.session.user_device_id || "-"} · {result.session.serial_number} · 시작 {formatServerTime(result.session.started_dt, "YYYY-MM-DD HH:mm", "")}
            </div>
            <div style={{ lineHeight: "1.9", marginBottom: "10px" }}>
              같음 <b>{result.same.toLocaleString()}</b> · 다름 <b style={{ color: result.diffs.length ? "#c62828" : "inherit" }}>{result.diffs.length.toLocaleString()}</b>
              {" "}· DB에만 <b>{result.onlyDb.length.toLocaleString()}</b>
              {" "}· 재계산에만 <b>{result.onlyRecalc.length.toLocaleString()}</b>
              {result.onlyRecalc.length > 0 && (result.onlyRecalcOld.length > 0
                ? <span style={{ color: "#c62828" }}> (그중 {result.onlyRecalcOld.length.toLocaleString()}개는 {RECENT_UNSAVED_MIN}분보다 오래됨 — 저장 누락)</span>
                : <span style={{ color: "#777" }}> (최근 미저장분 — 정상)</span>)}
              <br />
              {first !== null
                ? <>첫 어긋남 <b>{fmt(first)}</b> · 마지막 <b>{fmt(last)}</b> · 최대 차이 <b>{result.maxGap > 0 ? "+" : ""}{result.maxGap} mg/dL</b> · 구간 {result.ranges.length}개</>
                : <span style={{ color: "#2e7d32", fontWeight: "bold" }}>어긋난 점 없음 — DB 저장값 = 지금 표 재계산</span>}
            </div>

            <pre id="glucose-check-result" style={{ background: "#f6f5f9", padding: "8px 12px", fontSize: "12px", whiteSpace: "pre-wrap" }}>{machineResult}</pre>

            {result.ranges.length > 0 && (
              <table style={{ borderCollapse: "collapse", margin: "10px 0" }}>
                <thead><tr><th style={thBase}>구간</th><th style={thBase}>시작</th><th style={thBase}>끝</th><th style={thBase}>다른 점</th></tr></thead>
                <tbody>{result.ranges.map((r, i) => (
                  <tr key={i}><td style={tdBase}>{i + 1}</td><td style={tdBase}>{fmt(r.start)}</td><td style={tdBase}>{fmt(r.end)}</td><td style={tdBase}>{r.n.toLocaleString()}</td></tr>
                ))}</tbody>
              </table>
            )}

            {result.diffs.length > 0 && (
              <>
                <div style={{ display: "flex", gap: "8px", margin: "12px 0" }}>
                  <button style={btn(false)} onClick={downloadDiffCsv}>다른 점 CSV 다운로드</button>
                  <button style={btn(false, !result.session.user_device_id)} disabled={!result.session.user_device_id} onClick={() => setShowSql(!showSql)}>
                    {showSql ? "정리 SQL 닫기" : "정리 SQL 보기"}
                  </button>
                </div>
                {showSql && (
                  <textarea readOnly value={buildSql(result.session.user_device_id, first, last)}
                    style={{ width: "100%", height: "260px", fontFamily: "monospace", fontSize: "12px", marginBottom: "12px" }} />
                )}
                <table style={{ borderCollapse: "collapse" }}>
                  <thead><tr><th style={thBase}>시각</th><th style={thBase}>DB 저장값</th><th style={thBase}>지금 표 재계산</th><th style={thBase}>차이</th></tr></thead>
                  <tbody>{result.diffs.slice((diffPage - 1) * 50, diffPage * 50).map((x) => (
                    <tr key={x.t}><td style={tdBase}>{fmt(x.t)}</td><td style={tdBase}>{x.db}</td><td style={tdBase}>{x.re}</td>
                      <td style={{ ...tdBase, color: x.gap > 0 ? "#c62828" : "#1565c0" }}>{x.gap > 0 ? "+" : ""}{x.gap}</td></tr>
                  ))}</tbody>
                </table>
                <div style={{ display: "flex", gap: "6px", alignItems: "center", marginTop: "8px" }}>
                  <button style={btn(false, diffPage <= 1)} disabled={diffPage <= 1} onClick={() => setDiffPage(diffPage - 1)}>이전</button>
                  <span>{diffPage} / {diffPages}</span>
                  <button style={btn(false, diffPage >= diffPages)} disabled={diffPage >= diffPages} onClick={() => setDiffPage(diffPage + 1)}>다음</button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default withRouter(AdminGlucoseCheck);
